import StickyButton from '@/components/atom/buttons/StickyButton';
import ClickLogger from '@/components/common/ClickLogger';
import { EWebTreatmentRequestLog } from '@/entities/diagnosis/libs';
import {
  CERTIFICATION_CHANNEL_NAME,
  CERTIFICATION_MESSAGE_TYPE,
  CertificationMessage,
} from '@/shared/types/certification';
import { Button, useToast } from 'doctornow-design-system';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useRef } from 'react';

interface CertificationGuideStepProps {
  onCertificationSuccess: () => void;
}

const CertificationGuideStep = ({ onCertificationSuccess }: CertificationGuideStepProps) => {
  const router = useRouter();
  const { toast } = useToast();
  const lastProcessedTimestampRef = useRef<number>(0);

  const handleCertificationMessage = useCallback(
    (message: CertificationMessage) => {
      if (message.timestamp <= lastProcessedTimestampRef.current) {
        return;
      }

      lastProcessedTimestampRef.current = message.timestamp;

      if (message.type === CERTIFICATION_MESSAGE_TYPE.SUCCESS) {
        onCertificationSuccess();
        return;
      }

      toast({
        text: '본인인증에 실패했습니다. 다시 시도해주세요.',
        direction: 'bottom',
      });
      return;
    },
    [onCertificationSuccess, toast]
  );

  const handleCertification = useCallback(() => {
    const currentUrl = window.location.origin + router.asPath;
    const returnUrl = `${currentUrl}${currentUrl.includes('?') ? '&' : '?'}result=success`;
    const errorUrl = `${currentUrl}${currentUrl.includes('?') ? '&' : '?'}result=fail`;

    const certificationUrl = `/phone-verification?returnUrl=${encodeURIComponent(returnUrl)}&errorUrl=${encodeURIComponent(errorUrl)}`;

    const popupOptions =
      'width=480,height=812,top=100,left=100,' +
      'menubar=no,toolbar=no,status=no,scrollbars=yes,' +
      'resizable=yes,location=no';

    window.open(certificationUrl, 'certification_popup', popupOptions);
  }, [router]);

  useEffect(() => {
    const handlePostMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) {
        return;
      }

      const message = event.data as CertificationMessage;

      if (message && message.type && message.timestamp) {
        handleCertificationMessage(message);
      }
    };

    const handleBroadcastMessage = (event: MessageEvent<CertificationMessage>) => {
      handleCertificationMessage(event.data);
    };

    window.addEventListener('message', handlePostMessage);

    let channel: BroadcastChannel | null = null;
    if ('BroadcastChannel' in window) {
      channel = new BroadcastChannel(CERTIFICATION_CHANNEL_NAME);
      channel.onmessage = handleBroadcastMessage;
    }

    return () => {
      window.removeEventListener('message', handlePostMessage);
      if (channel) {
        channel.close();
      }
    };
  }, [handleCertificationMessage]);

  return (
    <div className='relative flex min-h-screen w-full flex-col bg-white'>
      <div className='box-border flex flex-col gap-[8px] px-[24px] pt-[12px] pb-0'>
        <h1 className='typography-ui-24sb text-gray-900'>
          마지막으로 안전한 진료를 위해
          <br />
          본인인증이 필요해요
        </h1>
        <p className='typography-ui-17r text-gray-600'>6개월마다 본인 명의 핸드폰 인증이 필요해요.</p>
      </div>

      <div className='box-border flex flex-1 flex-col items-center justify-center gap-[24px] px-[24px] pt-[40px] pb-[90px]'>
        <div className='relative h-[240px] w-[232px]'>
          <Image
            src='/images/certification/certification_guide_icon.png'
            alt='본인인증 안내'
            width={232}
            height={240}
            style={{ objectFit: 'cover' }}
            priority
          />
        </div>
      </div>

      <StickyButton>
        <div className='flex w-full flex-col gap-[12px]'>
          <ClickLogger params={{ eventName: EWebTreatmentRequestLog.ClickIdvSheet }}>
            <Button
              size='xl'
              buttonColor='primary'
              buttonStyle='contained'
              onClick={handleCertification}
              className='w-full'
            >
              본인인증
            </Button>
          </ClickLogger>
        </div>
      </StickyButton>
    </div>
  );
};

export default CertificationGuideStep;
